import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import PieChart from 'components/Charts/PieChart'
import ColumnChart from 'components/Charts/ColumnChart'
import HistoryDialog from 'components/Charts/HistoryDialog'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  InfoContainer,
  FilterContainer,
  LoadingContainer,
  SubTitle,
  InfoText,
  LinkText
} from '../../styles'

export default function EarningsByProfessional ({
  data: earnings,
  month = DateTime.now ().month,
  year = DateTime.now ().year
}) {
  const selectedDate = DateTime.local (year, month).setLocale ('pt-BR')
  const formattedDate = selectedDate.toFormat ('MMM yyyy')
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const typeFilterOptions = Dashboard.TYPE_FILTERS
  const [loading, setLoading] = useState (true)
  const [isMounted, setMounted] = useState (false)
  const [periodFilter, setPeriodFilter] = useState (null)
  const [typeFilter, setTypeFilter] = useState (null)
  const [showDetails, setShowDetails] = useState (false)
  const [earningsByMonth, setEarningsByMonth] = useState ([])

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (isMounted) {
      setPeriodFilter (periodFilterOptions[0])
      setTypeFilter (typeFilterOptions[0])
    }
  }, [isMounted])

  useEffect (() => {
    getData ()
  }, [periodFilter, typeFilter])

  const getData = async () => {
    if (showDetails) try {
      setLoading (true)

      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()
      const filters = `start_date=${startDate}&end_date=${endDate}&type=${typeFilter.value}`

      const { data: earningsByProfessional } = await cutileiApi.get (
        `/businesses/${businessId}/earnings_by_professional?${filters}`, requestConfig
      )

      const professionals = Dashboard.getKeysFromPeriod (earningsByProfessional)

      setEarningsByMonth ([
        ['Profissionais', ...professionals],
        ...Object.values (earningsByProfessional).map ((month, index) => ([
          Object.keys (earningsByProfessional)[index],
          ...professionals.map (name => month[name]?.totalValue || 0)
        ]))
      ])
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const toggleDetails = () => {
    if (!isMounted) setMounted (true)
    setShowDetails (!showDetails)
  }

  if (!earnings || Object.keys (earnings).length === 0) {
    return null
  }

  const earningsByProfessional = [
    ['Profissional', 'Valor (R$)'],
    ...Object.keys (Object.values (earnings)[0] || {}).map (data => ([
      data,
      earnings?.[formattedDate]?.[data]?.totalValue || 0
    ]))
  ]

  return (
    <InfoContainer>
      <SubTitle>Faturamento por profissional</SubTitle>
      <PieChart data={earningsByProfessional}/>
      <LinkText onClick={toggleDetails}>
        Ver histórico
      </LinkText>
      <HistoryDialog
        visible={showDetails}
        title='Faturamento por profissional'
        onClose={toggleDetails}
      >
        <FilterContainer style={{width: 520}}>
          <Select
            name='timeFilter'
            placeholder='Período...'
            value={periodFilter}
            options={periodFilterOptions}
            onChange={(_, option) => setPeriodFilter (option)}
          />
          <Select
            name='scheduleType'
            placeholder='Tipo de agendamento...'
            value={typeFilter}
            options={typeFilterOptions}
            onChange={(_, option) => setTypeFilter (option)}
          />
        </FilterContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráfico...</InfoText>
          </LoadingContainer>
        ) : (
          <ColumnChart
            data={earningsByMonth}
            options={{
              height: 450,
              chartArea: {
                top: 40, left: 110, width: '100%', height: '75%'
              }
            }}
          />
        )}
      </HistoryDialog>
    </InfoContainer>
  )
}
